import {
  X,
  Truck,
  Pencil,
  CheckCircle,
  XCircle,
  Calendar,
  MapPin,
  Phone,
  Mail,
  User,
  Hash,
  Building2,
  CreditCard,
  FileText,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import type { AssetVendor } from '../../../types/asset';

interface AssetVendorDetailsModalProps {
  open: boolean;
  onClose: () => void;
  vendor: AssetVendor | null;
  onEdit: (vendor: AssetVendor) => void;
}

function formatDate(value?: string): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function DetailItem({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-text-muted uppercase mb-1 flex items-center gap-1.5">
        <Icon size={12} />
        {label}
      </p>
      <p className="text-sm text-text break-words">{value}</p>
    </div>
  );
}

export function AssetVendorDetailsModal({ open, onClose, vendor, onEdit }: AssetVendorDetailsModalProps) {
  if (!open || !vendor) return null;

  const location = [vendor.location, vendor.city].filter(Boolean).join(', ');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-border bg-background/50 rounded-t-2xl">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Truck size={20} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-text truncate">{vendor.name}</h2>
              <p className="text-xs text-text-muted font-mono">{vendor.vendorCode || '—'}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-background rounded-xl transition-colors"
          >
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DetailItem icon={Hash} label="Vendor Code" value={vendor.vendorCode || '—'} />
            <div>
              <p className="text-xs font-medium text-text-muted uppercase mb-1">Status</p>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  vendor.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                }`}
              >
                {vendor.isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
                {vendor.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <DetailItem icon={User} label="Contact Person" value={vendor.contactPerson || '—'} />
            <DetailItem icon={Phone} label="Phone" value={vendor.phoneNumber || '—'} />
            <DetailItem icon={Mail} label="Email" value={vendor.email || '—'} />
            <DetailItem
              icon={CreditCard}
              label="Payment Terms"
              value={`${vendor.paymentTermsDays ?? 0} days`}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-border">
            <DetailItem icon={MapPin} label="Location" value={location || '—'} />
            <DetailItem icon={Building2} label="Address" value={vendor.address || '—'} />
            <DetailItem icon={FileText} label="Tax ID" value={vendor.taxId || '—'} />
          </div>

          <div className="pt-4 border-t border-border">
            <p className="text-xs font-medium text-text-muted uppercase mb-1 flex items-center gap-1.5">
              <FileText size={12} />
              Notes
            </p>
            <p className="text-sm text-text whitespace-pre-wrap">
              {vendor.notes || 'No notes provided.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-border">
            <DetailItem
              icon={Calendar}
              label="Created"
              value={<span className="flex items-center gap-2">{formatDate(vendor.createdAt)}</span>}
            />
            <DetailItem
              icon={Calendar}
              label="Last updated"
              value={<span className="flex items-center gap-2">{formatDate(vendor.updatedAt)}</span>}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border">
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button type="button" onClick={() => onEdit(vendor)}>
            <Pencil size={16} className="mr-2" />
            Edit
          </Button>
        </div>
      </div>
    </div>
  );
}
